import { useEffect, useRef, useState } from 'react';
import { Minus, Plus, X, Ban, Crosshair, MapPin } from 'lucide-react';
import { NavigationMap } from '@shared/components/navigation/navigation-map';
import { pxToWorld } from '@shared/lib/navigation-utils';
import type { NavigationMapInfo, RobotPose, WaypointMap } from '@shared/lib/api/navigation';
import { navigateToWaypoint, cancelNavigation, setInitialPose } from '@/lib/navigation-api';
import { useRobotStore } from '@/stores/useRobotStore';
import { useActionError } from '@/stores/useActionError';

export type DrawMode = 'pose' | 'goal';

interface NavigationOverlayProps {
  mapInfo: NavigationMapInfo;
  mapUrl: string;
  robotPose: RobotPose | null;
  path: Array<[number, number]>;
  waypoints: WaypointMap;
  /** Required for the default (desktop) layout's X close button. Omit when compact. */
  onClose?: () => void;
  /** When true, pre-activates pose draw mode and shows a localization-failure banner */
  locFailed?: boolean;
  /** Mobile/embedded layout: fills its parent, no header/border, no pill toolbar. */
  compact?: boolean;
  /** When provided, drawMode is controlled externally. */
  drawMode?: DrawMode | null;
  onDrawModeChange?: (mode: DrawMode | null) => void;
}

interface WaypointPopup {
  name: string;
  x: number;
  y: number;
}

export function NavigationOverlay({
  mapInfo,
  mapUrl,
  robotPose,
  path,
  waypoints,
  onClose,
  locFailed,
  compact,
  drawMode: propDrawMode,
  onDrawModeChange,
}: NavigationOverlayProps) {
  const navStatus = useRobotStore((s) => s.navStatus);
  const runAction = useActionError((s) => s.runAction);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [confirming, setConfirming] = useState(false);
  const [waypointPopup, setWaypointPopup] = useState<WaypointPopup | null>(null);
  const [internalDrawMode, setInternalDrawMode] = useState<DrawMode | null>(null);
  const drawMode: DrawMode | null = propDrawMode !== undefined ? propDrawMode : internalDrawMode;
  const setDrawMode = (next: DrawMode | null | ((prev: DrawMode | null) => DrawMode | null)) => {
    const resolved = typeof next === 'function' ? next(drawMode) : next;
    if (propDrawMode === undefined) setInternalDrawMode(resolved);
    onDrawModeChange?.(resolved);
  };
  const [dragArrow, setDragArrow] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{ mode: DrawMode; x: number; y: number; theta: number } | null>(null);
  const dragging = useRef(false);
  const lastPointer = useRef({ x: 0, y: 0 });
  const poseDragStart = useRef<{ cx: number; cy: number } | null>(null);
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{ dist: number; mid: { x: number; y: number } } | null>(null);
  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  const containerRef = useRef<HTMLDivElement>(null);
  zoomRef.current = zoom;
  panRef.current = pan;

  // Auto-activate pose draw mode only when locFailed with no pose estimate (truly lost).
  // When robotPose is non-null, ICP gave a best-guess — let the user confirm or override manually.
  useEffect(() => {
    if (locFailed && !robotPose) {
      setDrawMode('pose');
      setDragArrow(null);
      poseDragStart.current = null;
    }
  }, [locFailed, robotPose]);

  // Escape key: cancel draw mode, then pending confirm, then close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (drawMode) { setDrawMode(null); setDragArrow(null); poseDragStart.current = null; }
      else if (pendingConfirm) { setPendingConfirm(null); }
      else { onClose?.(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, drawMode, pendingConfirm]);

  /** Container-relative coords → world coords (accounts for pan/zoom + object-fit:contain) */
  const contToWorld = (cx: number, cy: number) => {
    if (!containerRef.current) return null;
    const { width: W, height: H } = containerRef.current.getBoundingClientRect();
    const s = Math.min(W / mapInfo.width, H / mapInfo.height);
    const innerX = (cx - panRef.current.x) / zoomRef.current;
    const innerY = (cy - panRef.current.y) / zoomRef.current;
    const mapPxX = (innerX - (W - mapInfo.width * s) / 2) / s;
    const mapPxY = innerY / s;
    if (mapPxX < 0 || mapPxX > mapInfo.width || mapPxY < 0 || mapPxY > mapInfo.height) return null;
    return pxToWorld(mapInfo, mapPxX, mapPxY);
  };

  /** Apply a multiplicative zoom factor centered at container-local point (cx, cy). */
  const zoomAt = (factor: number, cx: number, cy: number) => {
    const prevZoom = zoomRef.current;
    const nextZoom = Math.min(5, Math.max(1, prevZoom * factor));
    if (nextZoom === prevZoom) return;
    const scale = nextZoom / prevZoom;
    const p = panRef.current;
    setZoom(nextZoom);
    setPan({ x: cx - scale * (cx - p.x), y: cy - scale * (cy - p.y) });
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    zoomAt(e.deltaY > 0 ? 0.85 : 1.18, e.clientX - rect.left, e.clientY - rect.top);
  };

  // ── Desktop mouse handlers (verbatim from main) ──────────────────────────
  const handleMouseDown = (e: React.MouseEvent) => {
    if (drawMode) {
      const rect = containerRef.current!.getBoundingClientRect();
      poseDragStart.current = { cx: e.clientX - rect.left, cy: e.clientY - rect.top };
      return;
    }
    dragging.current = true;
    lastPointer.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (drawMode && poseDragStart.current) {
      const rect = containerRef.current!.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setDragArrow({ x1: poseDragStart.current.cx, y1: poseDragStart.current.cy, x2: cx, y2: cy });
      return;
    }
    if (!dragging.current) return;
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (drawMode && poseDragStart.current) {
      const rect = containerRef.current!.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const world = contToWorld(poseDragStart.current.cx, poseDragStart.current.cy);
      if (world) {
        const dx = cx - poseDragStart.current.cx;
        const dy = cy - poseDragStart.current.cy;
        const dist = Math.hypot(dx, dy);
        const theta = dist > 8 ? Math.atan2(-dy, dx) : 0;
        setPendingConfirm({ mode: drawMode, x: world.x, y: world.y, theta });
      }
      poseDragStart.current = null;
      setDragArrow(null);
      setDrawMode(null);
      return;
    }
    dragging.current = false;
  };

  // ── Mobile pointer handlers (touch + pinch zoom) ─────────────────────────
  function pinchOf(pts: { x: number; y: number }[]) {
    const [a, b] = pts;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    return { dist: Math.hypot(dx, dy), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = containerRef.current!.getBoundingClientRect();
    const local = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    e.currentTarget.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, local);

    if (pointersRef.current.size >= 2) {
      // Second finger lands → switch into pinch mode, abandon any single-pointer drag/draw.
      dragging.current = false;
      poseDragStart.current = null;
      setDragArrow(null);
      pinchRef.current = pinchOf(Array.from(pointersRef.current.values()));
      return;
    }

    if (drawMode) {
      poseDragStart.current = { cx: local.x, cy: local.y };
      return;
    }
    dragging.current = true;
    lastPointer.current = { x: e.clientX, y: e.clientY };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    const rect = containerRef.current!.getBoundingClientRect();
    const local = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    pointersRef.current.set(e.pointerId, local);

    if (pointersRef.current.size >= 2) {
      const cur = pinchOf(Array.from(pointersRef.current.values()));
      const prev = pinchRef.current;
      if (prev) {
        // Pinch: scale relative to the previous midpoint, then translate by the
        // midpoint delta so the gesture also pans naturally.
        const prevZoom = zoomRef.current;
        const nextZoom = Math.min(5, Math.max(1, prevZoom * (cur.dist / prev.dist)));
        const realFactor = nextZoom / prevZoom;
        const p = panRef.current;
        setZoom(nextZoom);
        setPan({
          x: prev.mid.x - realFactor * (prev.mid.x - p.x) + (cur.mid.x - prev.mid.x),
          y: prev.mid.y - realFactor * (prev.mid.y - p.y) + (cur.mid.y - prev.mid.y),
        });
      }
      pinchRef.current = cur;
      return;
    }

    if (drawMode && poseDragStart.current) {
      setDragArrow({ x1: poseDragStart.current.cx, y1: poseDragStart.current.cy, x2: local.x, y2: local.y });
      return;
    }
    if (!dragging.current) return;
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = containerRef.current!.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    pointersRef.current.delete(e.pointerId);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (pointersRef.current.size < 2) pinchRef.current = null;

    if (drawMode && poseDragStart.current) {
      const world = contToWorld(poseDragStart.current.cx, poseDragStart.current.cy);
      if (world) {
        const dx = cx - poseDragStart.current.cx;
        const dy = cy - poseDragStart.current.cy;
        const dist = Math.hypot(dx, dy);
        const theta = dist > 8 ? Math.atan2(-dy, dx) : 0;
        setPendingConfirm({ mode: drawMode, x: world.x, y: world.y, theta });
      }
      poseDragStart.current = null;
      setDragArrow(null);
      setDrawMode(null);
      return;
    }
    dragging.current = false;
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    dragging.current = false;
    poseDragStart.current = null;
    setDragArrow(null);
  };

  const handleWaypointClick = (name: string, e: React.MouseEvent) => {
    if (drawMode) return;
    const rect = containerRef.current!.getBoundingClientRect();
    setWaypointPopup({ name, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleNav = (dock: boolean) => {
    const popup = waypointPopup;
    if (!popup) return;
    const wp = waypoints[popup.name];
    if (!wp) return;
    setWaypointPopup(null);
    runAction(() => navigateToWaypoint({
      x: wp.x,
      y: wp.y,
      theta: wp.theta ?? 0,
      dock: dock && !!wp.dockable,
      station: dock && wp.dockable ? popup.name : undefined,
    }, popup.name));
  };

  const handleConfirm = () => {
    if (!pendingConfirm) return;
    const { mode, x, y, theta } = pendingConfirm;
    setPendingConfirm(null);
    if (mode === 'pose') {
      runAction(() => setInitialPose({ x, y, theta }));
    } else {
      runAction(() => navigateToWaypoint({ x, y, theta }));
    }
  };

  const handleConfirmCurrentPose = () => {
    if (!robotPose) return;
    setConfirming(true);
    runAction(() => setInitialPose({ x: robotPose.x, y: robotPose.y, theta: robotPose.theta ?? 0 }))
      .finally(() => setConfirming(false));
  };

  const toggleDrawMode = (mode: DrawMode) => {
    setDrawMode((m) => m === mode ? null : mode);
    setDragArrow(null);
    poseDragStart.current = null;
  };

  return (
    <div className={compact
      ? "relative h-full w-full bg-background flex flex-col overflow-hidden"
      : "absolute inset-3 z-10 bg-background flex flex-col rounded-2xl border border-border shadow-shell overflow-hidden verne-pop-in"}>
      {/* Header bar — desktop only */}
      {!compact && onClose && (
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
          <span className="text-sm font-semibold text-foreground">Navigation Map</span>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted hover:text-foreground hover:bg-surface-raised transition-colors"
            aria-label="Close map"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Localization banner */}
      {locFailed && (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-orange-500/90 text-white text-sm font-semibold shrink-0">
          {robotPose
            ? <span>Localization finished with low confidence — confirm the position shown on the map, or set a new pose below</span>
            : <span>Localization failed — click &amp; drag on the map to set the robot's position and orientation</span>
          }
          {robotPose && (
            <button
              disabled={confirming}
              onClick={(e) => { e.stopPropagation(); handleConfirmCurrentPose(); }}
              className="shrink-0 rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-orange-600 hover:bg-white/90 transition-colors disabled:opacity-50"
            >
              {confirming ? 'Confirming...' : 'Confirm Position'}
            </button>
          )}
        </div>
      )}

      {/* Map area —
       *  Desktop uses the mouse handlers (verbatim from main, which work
       *  correctly for click-and-drag pose / waypoint placement).
       *  Mobile (compact) uses pointer handlers with setPointerCapture +
       *  touch-action:none + pinch zoom. Switching is binary — no shared
       *  handler set — so neither path can regress the other. */}
      <div
        ref={containerRef}
        className={`flex-1 relative overflow-hidden select-none ${drawMode ? 'cursor-cell' : 'cursor-crosshair'} bg-white dark:bg-black`}
        style={compact
          ? { touchAction: 'none', ...(drawMode ? { outline: '2px solid #f97316', outlineOffset: '-2px' } : null) }
          : (drawMode ? { outline: '2px solid #f97316', outlineOffset: '-2px' } : undefined)}
        onWheel={handleWheel}
        {...(compact
          ? {
              onPointerDown: handlePointerDown,
              onPointerMove: handlePointerMove,
              onPointerUp: handlePointerUp,
              onPointerCancel: handlePointerCancel,
            }
          : {
              onMouseDown: handleMouseDown,
              onMouseMove: handleMouseMove,
              onMouseUp: handleMouseUp,
              onMouseLeave: () => { dragging.current = false; },
            })}
        onClick={() => { if (!drawMode) setWaypointPopup(null); }}
      >
        <div
          style={{
            transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            width: '100%',
            height: '100%',
          }}
        >
          <NavigationMap
            mapInfo={mapInfo}
            mapUrl={mapUrl}
            robotPose={robotPose}
            path={path}
            waypoints={waypoints}
            onWaypointClick={drawMode ? undefined : handleWaypointClick}
          />
        </div>

        {/* Drag arrow overlay (container-space coords) */}
        {dragArrow && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-20">
            <defs>
              <marker id="drag-arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                <polygon points="0 0, 8 3, 0 6" fill="#f97316" />
              </marker>
            </defs>
            <line
              x1={dragArrow.x1} y1={dragArrow.y1}
              x2={dragArrow.x2} y2={dragArrow.y2}
              stroke="#f97316" strokeWidth="2.5" strokeDasharray="6 3"
              markerEnd="url(#drag-arrowhead)"
            />
            <circle cx={dragArrow.x1} cy={dragArrow.y1} r="5" fill="#f97316" />
          </svg>
        )}

        {/* Waypoint popup */}
        {waypointPopup && (() => {
          const wp = waypoints[waypointPopup.name];
          if (!wp) return null;
          return (
            <div
              className="absolute z-20 pointer-events-auto"
              style={{
                left: waypointPopup.x,
                top: waypointPopup.y,
                transform: 'translateX(-50%) translateY(calc(-100% - 10px))',
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="bg-surface border border-border rounded-2xl shadow-xl p-3 flex flex-col gap-2 min-w-[200px]">
                <span className="text-base font-bold text-foreground px-1 truncate">{waypointPopup.name}</span>
                <button
                  onClick={() => handleNav(false)}
                  className="text-base font-semibold rounded-full px-5 py-3 bg-accent text-white hover:opacity-90 transition-opacity"
                >
                  Go
                </button>
                {wp.dockable && (
                  <button
                    onClick={() => handleNav(true)}
                    className="text-base font-semibold rounded-full px-5 py-3 bg-green-600 text-white hover:bg-green-500 transition-colors"
                  >
                    Go &amp; Dock
                  </button>
                )}
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0"
                style={{ borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid hsl(var(--color-border))' }}
              />
            </div>
          );
        })()}

        {/* Zoom indicator + (compact) buttons */}
        <div className="absolute top-3 right-3 flex items-center gap-1.5">
          {compact && (
            <div className="flex flex-col gap-1.5 pointer-events-auto">
              <button
                aria-label="Zoom in"
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = containerRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  zoomAt(1.3, rect.width / 2, rect.height / 2);
                }}
                className="w-9 h-9 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center border border-white/15 active:bg-black/75"
              >
                <Plus size={18} />
              </button>
              <button
                aria-label="Zoom out"
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = containerRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  zoomAt(1 / 1.3, rect.width / 2, rect.height / 2);
                }}
                className="w-9 h-9 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center border border-white/15 active:bg-black/75"
              >
                <Minus size={18} />
              </button>
            </div>
          )}
          <div className="rounded-xl bg-black/40 px-2.5 py-1.5 text-xs text-white/50 pointer-events-none self-start">
            {zoom.toFixed(1)}×
          </div>
        </div>

        {/* Top-left column: legend + pill buttons — desktop only */}
        {!compact && (
        <div className="absolute top-3 left-3 z-20 flex flex-col items-start gap-2">
          {/* Legend */}
          <div className="hidden md:flex items-center gap-3 rounded-xl bg-black/50 backdrop-blur-sm px-3 py-2 text-xs text-white/70 pointer-events-none">
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" /> Robot</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" /> Dock</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500" /> Waypoint</span>
            <span className="text-white/40">scroll to zoom · drag to pan · click waypoint to navigate</span>
          </div>

          {/* Pill buttons */}
          <div className="flex gap-2 pointer-events-auto">
            <button
              disabled={navStatus !== 'active'}
              onClick={(e) => { e.stopPropagation(); runAction(cancelNavigation); }}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold shadow-lg transition-all
                bg-black/70 backdrop-blur-sm border-2 border-white/15
                text-danger/70 disabled:opacity-30 disabled:cursor-not-allowed
                enabled:hover:bg-danger/20 enabled:hover:border-danger/50 enabled:hover:text-danger"
            >
              <Ban size={14} />
              Cancel Waypoint
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); toggleDrawMode('pose'); }}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold shadow-lg transition-all backdrop-blur-sm border-2 ${
                drawMode === 'pose'
                  ? 'bg-orange-500/95 border-orange-400 text-white'
                  : 'bg-black/70 border-white/15 text-white/85 hover:bg-white/15 hover:border-white/30 hover:text-white'
              }`}
            >
              <Crosshair size={14} />
              Set Current Pose
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); toggleDrawMode('goal'); }}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold shadow-lg transition-all backdrop-blur-sm border-2 ${
                drawMode === 'goal'
                  ? 'bg-accent/95 border-accent/70 text-white'
                  : 'bg-black/70 border-white/15 text-white/85 hover:bg-white/15 hover:border-white/30 hover:text-white'
              }`}
            >
              <MapPin size={14} />
              Custom Waypoint
            </button>
          </div>
        </div>
        )}

        {/* Bottom column: draw mode hint.
            Anchored at bottom-28 (not bottom-4) so it clears the e-stop /
            battery stack rendered by DashboardMiddle, which floats at z-30
            above this overlay's z-10 — without this offset any centered
            element here sits underneath the e-stop and is unclickable.
            The low-confidence Confirm Position action lives in the top
            banner now (single source, no bottom overlap risk). */}
        <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 pointer-events-none">
          {/* Draw mode hint */}
          {drawMode && (
            <div className="rounded-full bg-orange-500/90 backdrop-blur-sm px-4 py-1.5 text-sm text-white font-semibold whitespace-nowrap">
              {drawMode === 'pose' ? 'Set current pose' : 'Set custom waypoint'} — click &amp; drag to place, release to set heading
            </div>
          )}
        </div>
      </div>

      {/* Confirmation modal —
       *  Desktop uses main's original click-to-dismiss with stopPropagation
       *  on the inner card.
       *  Mobile uses pointerdown with target check (the synthetic click that
       *  follows a touch drag-release in draw mode would otherwise land on
       *  the backdrop and dismiss the modal we just opened). */}
      {pendingConfirm && (
        <div
          className="absolute inset-0 z-30 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          {...(compact
            ? { onPointerDown: (e: React.PointerEvent) => { if (e.target === e.currentTarget) setPendingConfirm(null); } }
            : { onClick: () => setPendingConfirm(null) })}
        >
          <div
            className="bg-surface border border-border rounded-2xl shadow-2xl p-5 w-72 flex flex-col gap-4"
            {...(compact ? {} : { onClick: (e: React.MouseEvent) => e.stopPropagation() })}
          >
            <div>
              <p className="text-sm font-semibold text-foreground">
                {pendingConfirm.mode === 'pose' ? 'Set current pose?' : 'Navigate to position?'}
              </p>
              <p className="text-xs text-muted mt-1">
                x: {pendingConfirm.x.toFixed(2)} m&nbsp;&nbsp;
                y: {pendingConfirm.y.toFixed(2)} m&nbsp;&nbsp;
                θ: {(pendingConfirm.theta * 180 / Math.PI).toFixed(0)}°
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setPendingConfirm(null)}
                className="rounded-lg px-4 py-1.5 text-xs font-medium bg-surface-raised border border-border text-foreground hover:bg-surface-strong transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className={`rounded-lg px-4 py-1.5 text-xs font-medium text-white transition ${
                  pendingConfirm.mode === 'pose' ? 'bg-orange-500 hover:bg-orange-400' : 'bg-accent hover:opacity-90'
                }`}
              >
                {pendingConfirm.mode === 'pose' ? 'Set Pose' : 'Navigate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
